import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LineChart as HeaderIcon } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'react-toastify';
import scadenzarioApi from '../services/scadenzarioApi';

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const monthsLabels = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatCompactKM = (value, withCurrency = false) => {
  const numericValue = Number(value || 0);
  const abs = Math.abs(numericValue);
  let formatted = '';
  if (abs >= 1000000) {
    formatted = `${(numericValue / 1000000).toFixed(1).replace('.', ',')}M`;
  } else if (abs >= 1000) {
    formatted = `${(numericValue / 1000).toFixed(1).replace('.', ',')}K`;
  } else {
    formatted = numericValue.toLocaleString('it-IT', { maximumFractionDigits: 0 });
  }
  return withCurrency ? `€ ${formatted}` : formatted;
};

const formatDelta = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return 'n/d';
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toLocaleString('it-IT', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

function ProiezioneTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.2,
        borderRadius: 1.2,
        minWidth: 220,
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
        Incassato: {formatCurrency(point.incassato)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569' }}>
        Δ prec.: {formatDelta(point.incassato_delta_pct)}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.8 }}>
        Da incassare: {formatCurrency(point.da_incassare)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569' }}>
        Δ prec.: {formatDelta(point.da_incassare_delta_pct)}
      </Typography>
      <Box sx={{ mt: 0.9, pt: 0.9, borderTop: '1px solid #E2E8F0' }}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          Gap: {formatCurrency(point.gap_value)}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function ScadenzarioKpiProiezionePiani({ embedded = false }) {
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ referenti: [], statiRate: [] });
  const [range, setRange] = useState({ fromDate: '', toDate: '', year: currentYear });
  const [totals, setTotals] = useState({ incassatoTotale: 0, daIncassareTotale: 0 });
  const [filters, setFilters] = useState({
    period: 'anno',
    year: String(currentYear),
    user_id: 'all',
    stato_rate: 'all',
  });

  const availableYears = useMemo(
    () => [currentYear - 2, currentYear - 1, currentYear, currentYear + 1],
    [currentYear]
  );
  const referenteOptions = useMemo(
    () => [{ id: 'all', name: 'Tutti' }, ...(meta.referenti || [])],
    [meta.referenti]
  );

  const fetchMeta = useCallback(async () => {
    try {
      const res = await scadenzarioApi.getMeta();
      setMeta({
        referenti: res.data?.referenti || [],
        statiRate: res.data?.statiRate || [],
      });
    } catch (_error) {
      toast.error('Errore caricamento filtri KPI');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period: filters.period };
      if (filters.year) params.year = Number(filters.year);
      if (filters.user_id !== 'all') params.user_id = Number(filters.user_id);
      if (filters.stato_rate !== 'all') params.stato_rate = filters.stato_rate;
      const res = await scadenzarioApi.getKpiProiezionePiani(params);
      setRows(res.data?.data || []);
      setRange({
        fromDate: res.data?.fromDate || '',
        toDate: res.data?.toDate || '',
        year: Number(res.data?.year || currentYear),
      });
      setTotals({
        incassatoTotale: Number(res.data?.incassatoTotale || 0),
        daIncassareTotale: Number(res.data?.daIncassareTotale || 0),
      });
    } catch (_error) {
      toast.error('Errore caricamento Proiezione Piani');
    } finally {
      setLoading(false);
    }
  }, [currentYear, filters.period, filters.stato_rate, filters.user_id, filters.year]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = useMemo(() => {
    if (filters.period === 'anno') {
      const base = monthsLabels.map((month, index) => ({
        label: month,
        incassato: 0,
        da_incassare: 0,
        bucket: index + 1,
      }));
      (rows || []).forEach((row) => {
        const idx = Number(row.bucket || 0) - 1;
        if (idx >= 0 && idx < base.length) {
          base[idx].incassato = Number(row.incassato || 0);
          base[idx].da_incassare = Number(row.da_incassare || 0);
        }
      });
      return base.map((item, index, arr) => {
        const prev = index > 0 ? arr[index - 1] : null;
        const incassatoDeltaPct =
          prev && Number(prev.incassato || 0) !== 0
            ? ((Number(item.incassato || 0) - Number(prev.incassato || 0)) /
                Number(prev.incassato || 0)) *
              100
            : 0;
        const daIncassareDeltaPct =
          prev && Number(prev.da_incassare || 0) !== 0
            ? ((Number(item.da_incassare || 0) - Number(prev.da_incassare || 0)) /
                Number(prev.da_incassare || 0)) *
              100
            : 0;
        return {
          ...item,
          incassato_delta_pct: incassatoDeltaPct,
          da_incassare_delta_pct: daIncassareDeltaPct,
          gap_value: Number(item.incassato || 0) - Number(item.da_incassare || 0),
        };
      });
    }
    if (filters.period === 'mese') {
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const mapped = Array.from({ length: daysInMonth }).map((_, idx) => {
        const day = idx + 1;
        const found = (rows || []).find((item) => Number(item.bucket) === day);
        return {
          label: String(day).padStart(2, '0'),
          incassato: Number(found?.incassato || 0),
          da_incassare: Number(found?.da_incassare || 0),
        };
      });
      return mapped.map((item, index, arr) => {
        const prev = index > 0 ? arr[index - 1] : null;
        const incassatoDeltaPct =
          prev && Number(prev.incassato || 0) !== 0
            ? ((Number(item.incassato || 0) - Number(prev.incassato || 0)) /
                Number(prev.incassato || 0)) *
              100
            : 0;
        const daIncassareDeltaPct =
          prev && Number(prev.da_incassare || 0) !== 0
            ? ((Number(item.da_incassare || 0) - Number(prev.da_incassare || 0)) /
                Number(prev.da_incassare || 0)) *
              100
            : 0;
        return {
          ...item,
          incassato_delta_pct: incassatoDeltaPct,
          da_incassare_delta_pct: daIncassareDeltaPct,
          gap_value: Number(item.incassato || 0) - Number(item.da_incassare || 0),
        };
      });
    }
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(now.getDate() + diffToMonday);
    const mapped = Array.from({ length: 7 }).map((_, idx) => {
      const current = new Date(monday);
      current.setDate(monday.getDate() + idx);
      const iso = current.toISOString().slice(0, 10);
      const found = (rows || []).find(
        (item) => String(item.bucket || '').slice(0, 10) === iso
      );
      return {
        label: current.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit' }),
        incassato: Number(found?.incassato || 0),
        da_incassare: Number(found?.da_incassare || 0),
      };
    });
    return mapped.map((item, index, arr) => {
      const prev = index > 0 ? arr[index - 1] : null;
      const incassatoDeltaPct =
        prev && Number(prev.incassato || 0) !== 0
          ? ((Number(item.incassato || 0) - Number(prev.incassato || 0)) /
              Number(prev.incassato || 0)) *
            100
          : 0;
      const daIncassareDeltaPct =
        prev && Number(prev.da_incassare || 0) !== 0
          ? ((Number(item.da_incassare || 0) - Number(prev.da_incassare || 0)) /
              Number(prev.da_incassare || 0)) *
            100
          : 0;
      return {
        ...item,
        incassato_delta_pct: incassatoDeltaPct,
        da_incassare_delta_pct: daIncassareDeltaPct,
        gap_value: Number(item.incassato || 0) - Number(item.da_incassare || 0),
      };
    });
  }, [filters.period, rows]);

  const activeFilterLabel = useMemo(() => {
    if (filters.period === 'anno') return `Anno intero ${range.year}`;
    if (filters.period === 'mese') return `Mese corrente (${range.fromDate} → ${range.toDate})`;
    return `Settimana corrente (${range.fromDate} → ${range.toDate})`;
  }, [filters.period, range.fromDate, range.toDate, range.year]);

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HeaderIcon size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Proiezione Piani
            </Typography>
            <Typography variant="body2" color="text.secondary">
              KPI incassato e da incassare sulle rate pianificate
            </Typography>
          </Box>
        </Box>
      ) : null}

      <StyledCard>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12} sx={{ order: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.2 }}>
                Filtro attivo: {activeFilterLabel}
              </Typography>
              <Box sx={{ height: 430 }}>
                {loading ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 18, left: 8, bottom: 6 }}
                    >
                      <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: '#64748B' }}
                        axisLine={{ stroke: '#CBD5E1' }}
                        tickLine={{ stroke: '#CBD5E1' }}
                      />
                      <YAxis
                        width={80}
                        tick={{ fontSize: 12, fill: '#64748B' }}
                        axisLine={{ stroke: '#CBD5E1' }}
                        tickLine={{ stroke: '#CBD5E1' }}
                        tickFormatter={(v) => formatCompactKM(v, true)}
                      />
                      <Tooltip content={<ProiezioneTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 13 }} />
                      <Line
                        type="monotone"
                        dataKey="incassato"
                        name="Incassato"
                        stroke="#10B981"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="da_incassare"
                        name="Da incassare"
                        stroke="#F59E0B"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Incassato totale
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#16A34A' }}>
                    {formatCurrency(totals.incassatoTotale)}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Da incassare totale
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#EF4444' }}>
                    {formatCurrency(totals.daIncassareTotale)}
                  </Typography>
                </Paper>
              </Box>
            </Grid>

            <Grid item xs={12} lg={12} sx={{ order: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Periodo</InputLabel>
                      <Select
                        label="Periodo"
                        value={filters.period}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, period: e.target.value }))
                        }
                      >
                        <MenuItem value="anno">Anno intero</MenuItem>
                        <MenuItem value="mese">Mese corrente</MenuItem>
                        <MenuItem value="settimana">Settimana corrente</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Anno</InputLabel>
                      <Select
                        label="Anno"
                        value={filters.year}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, year: e.target.value }))
                        }
                      >
                        {availableYears.map((item) => (
                          <MenuItem key={item} value={String(item)}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      size="small"
                      options={referenteOptions}
                      value={
                        referenteOptions.find(
                          (item) => String(item.id) === String(filters.user_id)
                        ) || referenteOptions[0]
                      }
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      onChange={(_event, value) =>
                        setFilters((prev) => ({
                          ...prev,
                          user_id: value ? String(value.id) : 'all',
                        }))
                      }
                      renderInput={(params) => <TextField {...params} label="Referente" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Stato rate</InputLabel>
                      <Select
                        label="Stato rate"
                        value={filters.stato_rate}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, stato_rate: e.target.value }))
                        }
                      >
                        <MenuItem value="all">Tutte le rate</MenuItem>
                        {(meta.statiRate || []).map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
                      <Button
                        color="inherit"
                        onClick={() =>
                          setFilters({
                            period: 'anno',
                            year: String(currentYear),
                            user_id: 'all',
                            stato_rate: 'all',
                          })
                        }
                      >
                        Azzera
                      </Button>
                      <Button variant="contained" color="warning" onClick={fetchData}>
                        Invia
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </StyledCard>
    </Box>
  );
}
